import { useMemo } from "react";

import { formatAwsDateString, formatCurrency } from "@covertree/utils";
import { Column, Row } from "@covertree/ui/table/types";

import { useAccessPattern } from "@hooks/useAccessPattern";
import { useOpenRenewalDetails } from "@hooks/actions/useOpenRenewalDetails";
import { useRenewalActions } from "@providers/RenewalActionsProvider";
import {
  RenewalListItemFragment,
  RenewalStatus,
} from "@covertree/agent-portal-graphql";
import { useOpenPolicyDetails } from "@hooks/actions/useOpenPolicyDetails";
import { mapRenewalStatus } from "@utils/mapRenewalStatus";
import { formatProductTypeName } from "@utils/formatPolicyType";
import { getOpenRenewalDetailsLabelByRenewalStatus } from "@utils/getOpenRenewalDetailsLabelByRenewalStatus";

import { RemovableRenewalColumn, createColumnValueChecker } from "./utils";

export const useMappedRenewals = (
  renewals: RenewalListItemFragment[] | undefined,
  columns: Column[],
  retryRenewalSendOffer: (renewalLocator: string) => void,
  retryIssuance: (renewalLocator: string) => void,
  downloadRenewalOfferDocument: (renewalLocator: string) => void,
  retryFailedRenewal: (renewalLocator: string) => void,
  resendRenewalOffer: (renewalLocator: string) => void
) => {
  const { canSeeDiscardedRenewals, canRetryFailedRenewal } = useAccessPattern();

  const openRenewalDetails = useOpenRenewalDetails();
  const openPolicyDetails = useOpenPolicyDetails();

  const { showRenewalDiscardModal } = useRenewalActions();

  return useMemo(() => {
    if (!renewals) {
      return [];
    }

    const isColumnDefinedForValue = createColumnValueChecker(columns);

    return renewals.map<Row>(({ unitAddress, renewalTerm, ...renewal }) => ({
      id: renewal.renewalLocator,
      data: [
        renewal.policyLocator,
        renewal.renewalLocator,
        renewal.policyholderName,
        [
          unitAddress.streetAddress,
          unitAddress.city,
          unitAddress.state,
          unitAddress.zipCode,
        ].join(" "),
        ...isColumnDefinedForValue(
          RemovableRenewalColumn.Type,
          formatProductTypeName(renewal.productTypeName)
        ),
        renewal.premium ? formatCurrency(renewal.premium) : "N/A",
        ...isColumnDefinedForValue(
          RemovableRenewalColumn.Status,
          mapRenewalStatus(renewal.status)
        ),
        ...isColumnDefinedForValue(
          RemovableRenewalColumn.NonRenewalReason,
          renewal.nonRenewalReason
        ),
        ...isColumnDefinedForValue(
          RemovableRenewalColumn.NonRenewalIssueDate,
          formatAwsDateString(renewal.nonRenewalIssueDate)
        ),
        [
          formatAwsDateString(renewalTerm.effectiveDate),
          formatAwsDateString(renewalTerm.endDate),
        ].join(" - "),
        ...isColumnDefinedForValue(
          RemovableRenewalColumn.RenewalDiscardedDate,
          formatAwsDateString(renewal.discardedDate)
        ),
        renewal.assignedAgentName ?? "Not Assigned",
        renewal.assignedAgencyName ?? "Not Assigned",
        ...isColumnDefinedForValue(
          RemovableRenewalColumn.RenewalIssueDate,
          renewal.issuedDate ? formatAwsDateString(renewal.issuedDate) : "N/A"
        ),
        formatAwsDateString(renewal.updatedDate),
        ...isColumnDefinedForValue(
          RemovableRenewalColumn.Description,
          renewal.description
        ),
      ],
      actionMenuItems: [
        {
          label: "View Policy Details",
          onClick: () => openPolicyDetails(renewal.policyLocator),
        },
        {
          label: getOpenRenewalDetailsLabelByRenewalStatus(renewal.status),
          onClick: () => openRenewalDetails(renewal.renewalLocator),
        },
        ...(canSeeDiscardedRenewals &&
        renewal.status === RenewalStatus.OfferSent
          ? [
              {
                label: "Discard Renewal",
                onClick: () => {
                  showRenewalDiscardModal(renewal.renewalLocator, true);
                },
              },
            ]
          : []),
        ...(canRetryFailedRenewal &&
        renewal.status === RenewalStatus.OfferSentFailed
          ? [
              {
                label: "Retry Send Offer",
                onClick: () => retryRenewalSendOffer(renewal.renewalLocator),
              },
            ]
          : []),
        ...(canRetryFailedRenewal && renewal.status === RenewalStatus.OfferSent
          ? [
              {
                label: "Resend Offer",
                onClick: () => resendRenewalOffer(renewal.renewalLocator),
              },
            ]
          : []),
        ...(canRetryFailedRenewal &&
        renewal?.status === RenewalStatus.PaymentFailed
          ? [
              {
                label: "Retry Issuance",
                onClick: () => retryIssuance(renewal.renewalLocator),
              },
            ]
          : []),
        ...(canRetryFailedRenewal &&
        [RenewalStatus.PricingFailed, RenewalStatus.UwFailed].includes(
          renewal.status
        )
          ? [
              {
                label: "Retry",
                onClick: () => retryFailedRenewal(renewal.renewalLocator),
              },
            ]
          : []),
        ...(renewal?.status === RenewalStatus.OfferSent
          ? [
              {
                label: "Download",
                onClick: () =>
                  downloadRenewalOfferDocument(renewal.renewalLocator),
              },
            ]
          : []),
      ],
    }));
  }, [renewals, columns, canRetryFailedRenewal, retryIssuance]);
};
